'use client'

import { useState, useEffect, useRef } from 'react'
import { Menu, X, ChevronDown, Smartphone, Globe, Cloud, Shield, Users, ShoppingCart, Blocks, Brain, Wifi, ArrowRight, Star, Zap, BookOpen, Clock, Building, Target } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/contexts/AuthContext'
import DynamicCompanyInfo from '@/components/DynamicCompanyInfo'
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion'
import ThemeToggle from '@/components/ThemeToggle'

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false)
  const [isServicesOpen, setIsServicesOpen] = useState(false)
  const [isAboutOpen, setIsAboutOpen] = useState(false)
  const [isCRMOpen, setIsCRMOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  
  const pathname = usePathname()
  const { scrollY } = useScroll()
  const headerBg = useTransform(scrollY, [0, 80], [0, 1])
  
  let user = null;
  let logout = () => {};
  
  try {
    const authContext = useAuth();
    user = authContext.user;
    logout = authContext.logout;
  } catch (error) {
    // Auth context not available
  }
  
  const isCRMPage = pathname?.startsWith('/crm')
  
  const aboutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const servicesTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const solutionsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const crmTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (aboutTimeoutRef.current) clearTimeout(aboutTimeoutRef.current)
      if (servicesTimeoutRef.current) clearTimeout(servicesTimeoutRef.current)
      if (solutionsTimeoutRef.current) clearTimeout(solutionsTimeoutRef.current)
      if (crmTimeoutRef.current) clearTimeout(crmTimeoutRef.current)
    }
  }, [])

  const handleAboutEnter = () => {
    if (aboutTimeoutRef.current) clearTimeout(aboutTimeoutRef.current)
    setIsAboutOpen(true)
  }
  const handleAboutLeave = () => {
    aboutTimeoutRef.current = setTimeout(() => setIsAboutOpen(false), 150)
  }
  const handleServicesEnter = () => {
    if (servicesTimeoutRef.current) clearTimeout(servicesTimeoutRef.current)
    setIsServicesOpen(true)
  }
  const handleServicesLeave = () => {
    servicesTimeoutRef.current = setTimeout(() => setIsServicesOpen(false), 150)
  }
  const handleSolutionsEnter = () => {
    if (solutionsTimeoutRef.current) clearTimeout(solutionsTimeoutRef.current)
    setIsSolutionsOpen(true)
  }
  const handleSolutionsLeave = () => {
    solutionsTimeoutRef.current = setTimeout(() => setIsSolutionsOpen(false), 150)
  }
  const handleCRMEnter = () => {
    if (crmTimeoutRef.current) clearTimeout(crmTimeoutRef.current)
    setIsCRMOpen(true)
  }
  const handleCRMLeave = () => {
    crmTimeoutRef.current = setTimeout(() => setIsCRMOpen(false), 150)
  }

  const navItems = [{ name: 'Home', href: '/' }]

  const crmItems = [
    { name: 'Dashboard', href: '/crm/dashboard', icon: <Target className="w-5 h-5" />, description: 'Overview of your business metrics', badge: 'Main', color: 'from-sky-500 to-blue-600' },
    { name: 'Employees', href: '/crm/employees', icon: <Users className="w-5 h-5" />, description: 'Manage your team members', badge: 'HR', color: 'from-blue-500 to-cyan-600' },
    { name: 'Clients', href: '/crm/clients', icon: <Building className="w-5 h-5" />, description: 'Client management & tracking', badge: 'CRM', color: 'from-green-500 to-teal-600' },
    { name: 'Projects', href: '/crm/projects', icon: <BookOpen className="w-5 h-5" />, description: 'Project progress tracking', badge: 'PM', color: 'from-purple-500 to-indigo-600' },
    { name: 'Tasks', href: '/crm/tasks', icon: <Zap className="w-5 h-5" />, description: 'Task assignment & monitoring', badge: 'Tasks', color: 'from-orange-500 to-red-600' },
    { name: 'Attendance', href: '/crm/attendance', icon: <Clock className="w-5 h-5" />, description: 'Employee time tracking', badge: 'Time', color: 'from-indigo-500 to-purple-600' },
    { name: 'Reports', href: '/crm/reports', icon: <Star className="w-5 h-5" />, description: 'Analytics & performance', badge: 'Analytics', color: 'from-red-500 to-pink-600' }
  ]

  const aboutItems = [
    { name: 'About Us', href: '/about', icon: <Users className="w-5 h-5" />, description: 'Our journey, mission, and commitment', badge: 'New', color: 'from-sky-500 to-blue-600' },
    { name: 'Our Team', href: '/about/team', icon: <Users className="w-5 h-5" />, description: 'Meet our talented professionals', badge: '', color: 'from-blue-500 to-cyan-600' },
    { name: 'Testimonials', href: '/about/testimonials', icon: <Star className="w-5 h-5" />, description: 'Real success stories from clients', badge: 'Popular', color: 'from-amber-500 to-orange-600' },
    { name: 'Career', href: '/about/career', icon: <Zap className="w-5 h-5" />, description: 'Join our team and shape the future', badge: 'Hiring', color: 'from-green-500 to-teal-600' }
  ]

  const solutions = [
    { name: 'Hospital Management', href: '/solutions/hospital-management', icon: <Shield className="w-5 h-5" />, description: 'Complete healthcare management system', badge: 'Healthcare', color: 'from-red-500 to-pink-600' },
    { name: 'School Management', href: '/solutions/school-management', icon: <BookOpen className="w-5 h-5" />, description: 'Student information system', badge: 'Education', color: 'from-blue-500 to-indigo-600' },
    { name: 'College Management', href: '/solutions/college-management', icon: <Users className="w-5 h-5" />, description: 'Higher education management', badge: 'Higher Ed', color: 'from-purple-500 to-indigo-600' },
    { name: 'Gym Management', href: '/solutions/gym-management', icon: <Zap className="w-5 h-5" />, description: 'Fitness center management', badge: 'Fitness', color: 'from-orange-500 to-red-600' },
    { name: 'Marriage Bureau', href: '/solutions/marriage-bureau', icon: <Users className="w-5 h-5" />, description: 'Matrimonial platform with matching', badge: 'Matrimonial', color: 'from-pink-500 to-rose-600' },
    { name: 'LMS (Learning)', href: '/solutions/lms', icon: <Globe className="w-5 h-5" />, description: 'Learning Management System', badge: 'E-Learning', color: 'from-green-500 to-teal-600' },
    { name: 'Ecommerce Platform', href: '/solutions/ecommerce-platform', icon: <ShoppingCart className="w-5 h-5" />, description: 'Ready-to-deploy online store', badge: 'E-Commerce', color: 'from-emerald-500 to-green-600' },
    { name: 'Cab Service', href: '/solutions/cab-service', icon: <Smartphone className="w-5 h-5" />, description: 'Ride-hailing platform', badge: 'Transport', color: 'from-yellow-500 to-orange-600' },
    { name: 'On-Demand Services', href: '/solutions/on-demand-services', icon: <Clock className="w-5 h-5" />, description: 'Service marketplace platform', badge: 'Marketplace', color: 'from-cyan-500 to-blue-600' },
    { name: 'Real Estate CRM', href: '/solutions/real-estate-crm', icon: <Building className="w-5 h-5" />, description: 'Property management & CRM', badge: 'Real Estate', color: 'from-gray-500 to-gray-700' },
    { name: 'CRM System', href: '/solutions/crm-system', icon: <Target className="w-5 h-5" />, description: 'Customer relationship management', badge: 'Business', color: 'from-indigo-500 to-purple-600' }
  ]

  const services = [
    { name: 'Mobile App Development', href: '/services/mobile-app-development', icon: <Smartphone className="w-5 h-5" />, description: 'iOS, Android & Cross-platform', badge: 'Hot', color: 'from-pink-500 to-rose-600' },
    { name: 'Web Development', href: '/services/web-development', icon: <Globe className="w-5 h-5" />, description: 'Modern websites & web apps', badge: 'Popular', color: 'from-blue-500 to-indigo-600' },
    { name: 'eCommerce Development', href: '/services/ecommerce-development', icon: <ShoppingCart className="w-5 h-5" />, description: 'Online stores & marketplaces', badge: 'Trending', color: 'from-green-500 to-emerald-600' },
    { name: 'Blockchain Development', href: '/services/blockchain-development', icon: <Blocks className="w-5 h-5" />, description: 'Smart contracts & DApps', badge: 'Web3', color: 'from-orange-500 to-red-600' },
    { name: 'AI & ML Services', href: '/services/ai-ml-services', icon: <Brain className="w-5 h-5" />, description: 'Intelligent automation & ML', badge: 'AI', color: 'from-purple-500 to-indigo-600' },
    { name: 'DevOps & Cloud', href: '/services/devops-cloud', icon: <Cloud className="w-5 h-5" />, description: 'Cloud migration & CI/CD', badge: 'DevOps', color: 'from-cyan-500 to-blue-600' },
    { name: 'IoT & Embedded', href: '/services/iot-embedded', icon: <Wifi className="w-5 h-5" />, description: 'Internet of Things solutions', badge: 'IoT', color: 'from-teal-500 to-cyan-600' },
    { name: 'Cybersecurity', href: '/services/cybersecurity', icon: <Shield className="w-5 h-5" />, description: 'Security assessments & protection', badge: 'Secure', color: 'from-red-500 to-pink-600' }
  ]

  return (
    <motion.header 
      className={`fixed w-full top-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl shadow-lg border-b border-gray-200 dark:border-slate-700' 
          : 'bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-100 dark:border-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="container mx-auto px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center group">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <DynamicCompanyInfo 
                type="logo" 
                variant="dark"
                className="max-h-8 w-auto object-contain"
              >
                {(companyInfo: any) => (
                  companyInfo?.logo?.dark ? (
                    <img 
                      src={companyInfo.logo.dark} 
                      alt={companyInfo.companyName || 'Logo'} 
                      className="max-h-8 w-auto object-contain" 
                    />
                  ) : (
                    <div className="h-10 w-10 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-sky-500/30">
                      <span className="text-white font-bold text-lg">
                        {companyInfo?.companyName?.charAt(0).toUpperCase() || 'C'}
                      </span>
                    </div>
                  )
                )}
              </DynamicCompanyInfo>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {!isCRMPage ? (
              <>
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={`relative px-4 py-2 transition-colors duration-200 font-medium text-sm group ${
                      scrolled ? 'text-white/90 hover:text-white' : 'text-slate-700 hover:text-sky-600'
                    }`}
                  >
                    {item.name}
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-sky-400 to-blue-600 group-hover:w-3/4 transition-all duration-300 rounded-full"></span>
                  </Link>
                ))}
                
                {/* About Dropdown */}
                <div className="relative" onMouseEnter={handleAboutEnter} onMouseLeave={handleAboutLeave}>
                  <button className={`flex items-center px-4 py-2 transition-colors duration-200 font-medium text-sm group ${
                    scrolled ? 'text-white/90 hover:text-white' : 'text-slate-700 hover:text-sky-600'
                  }`}>
                    About
                    <ChevronDown className={`ml-1 w-3.5 h-3.5 transition-transform duration-300 ${isAboutOpen ? 'rotate-180' : ''}`} />
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-sky-400 to-blue-600 group-hover:w-3/4 transition-all duration-300 rounded-full"></span>
                  </button>
                </div>
                
                {/* Services Dropdown */}
                <div className="relative" onMouseEnter={handleServicesEnter} onMouseLeave={handleServicesLeave}>
                  <button className={`flex items-center px-4 py-2 transition-colors duration-200 font-medium text-sm group text-slate-700 dark:text-slate-200 hover:text-sky-600 dark:hover:text-sky-400`}>
                    Services
                    <ChevronDown className={`ml-1 w-3.5 h-3.5 transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`} />
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-sky-400 to-blue-600 group-hover:w-3/4 transition-all duration-300 rounded-full"></span>
                  </button>
                </div>
                
                {/* Solutions Dropdown */}
                <div className="relative" onMouseEnter={handleSolutionsEnter} onMouseLeave={handleSolutionsLeave}>
                  <button className={`flex items-center px-4 py-2 transition-colors duration-200 font-medium text-sm group text-slate-700 dark:text-slate-200 hover:text-sky-600 dark:hover:text-sky-400`}>
                    Solutions
                    <ChevronDown className={`ml-1 w-3.5 h-3.5 transition-transform duration-300 ${isSolutionsOpen ? 'rotate-180' : ''}`} />
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-sky-400 to-blue-600 group-hover:w-3/4 transition-all duration-300 rounded-full"></span>
                  </button>
                </div>

                <Link href="/blog" className={`relative px-4 py-2 transition-colors duration-200 font-medium text-sm group text-slate-700 dark:text-slate-200 hover:text-sky-600 dark:hover:text-sky-400`}>
                  Blog
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-sky-400 to-blue-600 group-hover:w-3/4 transition-all duration-300 rounded-full"></span>
                </Link>

                <Link href="/recruitment" className={`relative px-4 py-2 transition-colors duration-200 font-medium text-sm group text-slate-700 dark:text-slate-200 hover:text-sky-600 dark:hover:text-sky-400`}>
                  Recruitment
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-sky-400 to-blue-600 group-hover:w-3/4 transition-all duration-300 rounded-full"></span>
                </Link>
              </>
            ) : (
              <>
                <Link href="/" className={`relative px-4 py-2 transition-colors duration-200 font-medium text-sm group text-slate-700 dark:text-slate-200 hover:text-sky-600 dark:hover:text-sky-400`}>
                  Back to Website
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-sky-400 to-blue-600 group-hover:w-3/4 transition-all duration-300 rounded-full"></span>
                </Link>
                
                <div className="relative" onMouseEnter={handleCRMEnter} onMouseLeave={handleCRMLeave}>
                  <button className={`flex items-center px-4 py-2 transition-colors duration-200 font-medium text-sm group text-slate-700 dark:text-slate-200 hover:text-sky-600 dark:hover:text-sky-400`}>
                    CRM Modules
                    <ChevronDown className={`ml-1 w-3.5 h-3.5 transition-transform duration-300 ${isCRMOpen ? 'rotate-180' : ''}`} />
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-sky-400 to-blue-600 group-hover:w-3/4 transition-all duration-300 rounded-full"></span>
                  </button>
                </div>

                {user && (
                  <div className="flex items-center space-x-3 ml-4 pl-4 border-l border-sky-200 dark:border-slate-700">
                    <ThemeToggle />
                    <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-600 rounded-full flex items-center justify-center shadow-md">
                      <span className="text-white text-sm font-semibold">{user?.name?.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-900 dark:text-slate-100">{user?.name}</p>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400">{user?.type}</p>
                    </div>
                    <button onClick={logout} className="text-red-500 hover:text-red-700 transition-colors text-xs font-medium ml-2">
                      Logout
                    </button>
                  </div>
                )}
              </>
            )}
          </nav>

          {/* CTA Button */}
          {!isCRMPage && (
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="hidden lg:block">
              <Link 
                href="/contact" 
                className="inline-flex items-center px-6 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-xl font-semibold text-sm hover:shadow-xl hover:shadow-sky-500/30 transition-all duration-300 group"
              >
                <span>Contact Us</span>
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-300" />
              </Link>
            </motion.div>
          )}

          {/* Mobile Menu Button */}
          <motion.button
            className="lg:hidden p-2 text-slate-600 dark:text-slate-300 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-slate-800 rounded-xl transition-colors duration-200"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            whileTap={{ scale: 0.9 }}
          >
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="lg:hidden bg-white/95 backdrop-blur-2xl border-t border-sky-100"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <nav className="container mx-auto px-6 py-4 flex flex-col space-y-2">
              {navItems.map((item, i) => (
                <motion.div key={item.name} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                  <Link href={item.href} className="block text-slate-700 hover:text-sky-600 font-medium py-2 px-4 rounded-lg hover:bg-sky-50 transition-all" onClick={() => setIsMenuOpen(false)}>
                    {item.name}
                  </Link>
                </motion.div>
              ))}
              
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="py-2 px-4">
                <div className="font-semibold text-slate-900 mb-2 text-sm">About</div>
                <div className="space-y-1 pl-3 border-l-2 border-sky-200">
                  {aboutItems.map((item) => (
                    <Link key={item.href} href={item.href} className="block text-slate-600 hover:text-sky-600 text-sm py-1.5 transition-colors" onClick={() => setIsMenuOpen(false)}>
                      {item.name}
                    </Link>
                  ))}
                </div>
              </motion.div>
              
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }} className="py-2 px-4">
                <div className="font-semibold text-slate-900 mb-2 text-sm">Services</div>
                <div className="space-y-1 pl-3 border-l-2 border-sky-200">
                  {services.map((service) => (
                    <Link key={service.href} href={service.href} className="block text-slate-600 hover:text-sky-600 text-sm py-1.5 transition-colors" onClick={() => setIsMenuOpen(false)}>
                      {service.name}
                    </Link>
                  ))}
                </div>
              </motion.div>
              
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="py-2 px-4">
                <div className="font-semibold text-slate-900 mb-2 text-sm">Solutions</div>
                <div className="space-y-1 pl-3 border-l-2 border-sky-200">
                  {solutions.map((solution) => (
                    <Link key={solution.href} href={solution.href} className="block text-slate-600 hover:text-sky-600 text-sm py-1.5 transition-colors" onClick={() => setIsMenuOpen(false)}>
                      {solution.name}
                    </Link>
                  ))}
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 }}>
                <Link href="/blog" className="block text-slate-700 hover:text-sky-600 font-medium py-2 px-4 rounded-lg hover:bg-sky-50 transition-all" onClick={() => setIsMenuOpen(false)}>Blog</Link>
              </motion.div>
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
                <Link href="/recruitment" className="block text-slate-700 hover:text-sky-600 font-medium py-2 px-4 rounded-lg hover:bg-sky-50 transition-all" onClick={() => setIsMenuOpen(false)}>Recruitment</Link>
              </motion.div>
              
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="pt-3">
                <Link href="/contact" className="block text-center px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-xl font-semibold shadow-lg shadow-sky-500/25 hover:shadow-xl transition-all" onClick={() => setIsMenuOpen(false)}>
                  Contact Us
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* About Mega Menu */}
      <AnimatePresence>
        {isAboutOpen && (
          <motion.div 
            className="fixed left-0 w-screen bg-white/95 backdrop-blur-2xl shadow-2xl shadow-sky-100/50 z-40 border-t border-sky-100/50"
            onMouseEnter={handleAboutEnter}
            onMouseLeave={handleAboutLeave}
            style={{ top: 'calc(100% - 1px)' }}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="max-w-6xl mx-auto px-8 py-8">
              <div className="text-center mb-6">
                <DynamicCompanyInfo type="companyName">
                  {(companyInfo: any) => (
                    <h2 className="text-2xl font-bold text-slate-900">About {companyInfo?.companyName || 'Our Company'}</h2>
                  )}
                </DynamicCompanyInfo>
                <p className="text-slate-500 text-sm mt-1">Leading digital transformation with innovative solutions</p>
              </div>
              <div className="grid grid-cols-4 gap-4">
                {aboutItems.map((item, index) => (
                  <motion.div key={index} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                    <Link
                      href={item.href}
                      className="group block p-5 rounded-2xl bg-sky-50/50 hover:bg-white border border-transparent hover:border-sky-100 hover:shadow-lg hover:shadow-sky-100/50 transition-all duration-300"
                      onClick={() => setIsAboutOpen(false)}
                    >
                      <div className={`w-10 h-10 bg-gradient-to-r ${item.color} rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform shadow-md`}>
                        {item.icon}
                      </div>
                      <h3 className="font-semibold text-slate-900 group-hover:text-sky-600 transition-colors text-sm mb-1">{item.name}</h3>
                      <p className="text-xs text-slate-500 leading-relaxed">{item.description}</p>
                      {item.badge && (
                        <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-gradient-to-r ${item.color}`}>{item.badge}</span>
                      )}
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Services Mega Menu */}
      <AnimatePresence>
        {isServicesOpen && (
          <motion.div 
            className="fixed left-0 w-screen bg-white/95 backdrop-blur-2xl shadow-2xl shadow-sky-100/50 z-40 border-t border-sky-100/50 max-h-[80vh] overflow-y-auto"
            onMouseEnter={handleServicesEnter}
            onMouseLeave={handleServicesLeave}
            style={{ top: 'calc(100% - 1px)' }}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="max-w-6xl mx-auto px-8 py-8">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Our Expert Services</h2>
                <p className="text-slate-500 text-sm mt-1">Comprehensive technology services for your business</p>
              </div>
              <div className="grid grid-cols-4 gap-4">
                {services.map((service, index) => (
                  <motion.div key={index} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }}>
                    <Link
                      href={service.href}
                      className="group block p-5 rounded-2xl bg-sky-50/50 hover:bg-white border border-transparent hover:border-sky-100 hover:shadow-lg hover:shadow-sky-100/50 transition-all duration-300"
                      onClick={() => setIsServicesOpen(false)}
                    >
                      <div className={`w-10 h-10 bg-gradient-to-r ${service.color} rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform shadow-md`}>
                        {service.icon}
                      </div>
                      <h3 className="font-semibold text-slate-900 group-hover:text-sky-600 transition-colors text-sm mb-1">{service.name}</h3>
                      <p className="text-xs text-slate-500 leading-relaxed">{service.description}</p>
                      <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-gradient-to-r ${service.color}`}>{service.badge}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Solutions Mega Menu */}
      <AnimatePresence>
        {isSolutionsOpen && (
          <motion.div 
            className="fixed left-0 w-screen bg-white/95 backdrop-blur-2xl shadow-2xl shadow-sky-100/50 z-40 border-t border-sky-100/50 max-h-[80vh] overflow-y-auto"
            onMouseEnter={handleSolutionsEnter}
            onMouseLeave={handleSolutionsLeave}
            style={{ top: 'calc(100% - 1px)' }}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="max-w-6xl mx-auto px-8 py-8">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Ready-Made Solutions</h2>
                <p className="text-slate-500 text-sm mt-1">Pre-built solutions ready for deployment</p>
              </div>
              <div className="grid grid-cols-4 gap-4">
                {solutions.map((solution, index) => (
                  <motion.div key={index} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }}>
                    <Link
                      href={solution.href}
                      className="group block p-5 rounded-2xl bg-sky-50/50 hover:bg-white border border-transparent hover:border-sky-100 hover:shadow-lg hover:shadow-sky-100/50 transition-all duration-300"
                      onClick={() => setIsSolutionsOpen(false)}
                    >
                      <div className={`w-10 h-10 bg-gradient-to-r ${solution.color} rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform shadow-md`}>
                        {solution.icon}
                      </div>
                      <h3 className="font-semibold text-slate-900 group-hover:text-sky-600 transition-colors text-sm mb-1">{solution.name}</h3>
                      <p className="text-xs text-slate-500 leading-relaxed">{solution.description}</p>
                      <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-gradient-to-r ${solution.color}`}>{solution.badge}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CRM Mega Menu */}
      <AnimatePresence>
        {isCRMOpen && (
          <motion.div 
            className="fixed left-0 w-screen bg-white/95 backdrop-blur-2xl shadow-2xl shadow-sky-100/50 z-40 border-t border-sky-100/50"
            onMouseEnter={handleCRMEnter}
            onMouseLeave={handleCRMLeave}
            style={{ top: 'calc(100% - 1px)' }}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="max-w-5xl mx-auto px-8 py-8">
              <div className="grid grid-cols-4 gap-4">
                {crmItems.map((item, index) => (
                  <motion.div key={index} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                    <Link
                      href={item.href}
                      className="group block p-5 rounded-2xl bg-sky-50/50 hover:bg-white border border-transparent hover:border-sky-100 hover:shadow-lg transition-all duration-300"
                      onClick={() => setIsCRMOpen(false)}
                    >
                      <div className={`w-10 h-10 bg-gradient-to-r ${item.color} rounded-xl flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform shadow-md`}>
                        {item.icon}
                      </div>
                      <h3 className="font-semibold text-slate-900 group-hover:text-sky-600 transition-colors text-sm mb-1">{item.name}</h3>
                      <p className="text-xs text-slate-500">{item.description}</p>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  )
}
